import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Component, Suspense } from 'react';
import { lazyWithRetry } from './lazyWithRetry';
/**
 * Fallback component shown while the icon is loading
 * This is a simple invisible placeholder that maintains the icon's typical dimensions
 */ const IconFallback = ({ size = 16 })=>/*#__PURE__*/ _jsx("svg", {
        width: size,
        height: size,
        viewBox: `0 0 ${size} ${size}`,
        xmlns: "http://www.w3.org/2000/svg",
        "aria-hidden": "true",
        style: {
            display: 'inline-block',
            verticalAlign: 'middle'
        }
    });
// Absorbs a failed icon chunk so it can't escalate to an app-level crash
// (lazyWithRetry already logged it to Datadog).
class IconLoadErrorBoundary extends Component {
    // getDerivedStateFromError alone renders the fallback; no side effect needed
    // since lazyWithRetry already logged the failure to Datadog.
    static getDerivedStateFromError() {
        return {
            failed: true
        };
    }
    render() {
        const { failed } = this.state;
        const { size, children } = this.props;
        if (failed) {
            return /*#__PURE__*/ _jsx(IconFallback, {
                size: size
            });
        }
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: children
        });
    }
    constructor(props){
        super(props);
        this.state = {
            failed: false
        };
    }
}
/**
 * Wrapper for lazy-loaded Carbon icons that provides a fallback
 * This allows icons to be used with styled-components without causing reference errors
 *
 * @param importFn - Function that returns a promise for the icon module
 * @param size - Icon size (16, 20, 24, 32) for the fallback
 *
 * @example
 * const Add16 = lazyLoadCarbonIcon(() => import('@carbon/icons-react/es/add/16'), 16);
 * const StyledIcon = styled(Add16)`color: blue;`; // Works without errors!
 */ export function lazyLoadCarbonIcon(importFn, size = 16) {
    const LazyIcon = lazyWithRetry(importFn);
    // Return a component that wraps the lazy icon with Suspense
    const IconWithFallback = (props)=>/*#__PURE__*/ _jsx(IconLoadErrorBoundary, {
            size: size,
            children: /*#__PURE__*/ _jsx(Suspense, {
                fallback: /*#__PURE__*/ _jsx(IconFallback, {
                    size: size
                }),
                children: /*#__PURE__*/ _jsx(LazyIcon, {
                    ...props
                })
            })
        });
    // Preserve displayName for debugging
    IconWithFallback.displayName = 'LazyIcon';
    return IconWithFallback;
}
